/**
 * Comprehensive product module information for Explanatum platform
 * Used by ModuleGuide.tsx and ProductAssistantWidget.tsx
 */

export interface ProductModule {
  title: string;
  description: string;
  to: string;
  cta: string;
  category?: 'strategy' | 'marketing' | 'sales' | 'finance' | 'operations' | 'hr' | 'tools';
}

export const modules: ProductModule[] = [
  {
    title: 'AI Business Consultant',
    description:
      'An always-on advisor that remembers your conversations, reads live CRM metrics, and executes tasks like LinkedIn posts directly from chat.',
    to: '/modules/business-consultant',
    cta: 'Explore the AI Business Consultant',
    category: 'strategy',
  },
  {
    title: 'AI Business Plan Generator',
    description:
      'Create investor-ready plans in minutes with structured sections, market insights, and export-ready formats synced to your workspace.',
    to: '/modules/business-plan',
    cta: 'Explore the AI Business Plan Generator',
    category: 'strategy',
  },
  {
    title: 'Brand Kit Creator',
    description:
      'Complete brand identity suite: business name, AI logo, email signature, letterhead, and compliments slips. 6 professional tools in one seamless workflow.',
    to: '/modules/brand-kit-creator',
    cta: 'Explore the Brand Kit Creator',
    category: 'marketing',
  },
  {
    title: 'AI Customer CRM',
    description:
      'Centralize contacts, deals, tasks, and AI-guided follow-ups while staying connected to WhatsApp, email, and the Business Consultant.',
    to: '/modules/customer-crm',
    cta: 'Explore the AI Customer CRM',
    category: 'sales',
  },
  {
    title: 'AI LinkedIn Enhancer',
    description:
      'Unify founder and company LinkedIn messaging, automate updates, and hand off approved copy to outreach, recruiting, and investor workflows.',
    to: '/modules/linkedin-enhancer',
    cta: 'Explore the AI LinkedIn Enhancer',
    category: 'marketing',
  },
  {
    title: 'AI Press Release Generator',
    description:
      'Draft AP-style press releases with quotes, boilerplates, and media contacts in minutes, then push them to your consultant and outreach tools.',
    to: '/modules/press-release-generator',
    cta: 'Explore the AI Press Release Generator',
    category: 'marketing',
  },
  {
    title: 'AI Social Media Planner',
    description:
      'Plan, schedule, and analyze social campaigns with AI-generated posts, calendar automation, and analytics synced to your CRM.',
    to: '/modules/social-media-planner',
    cta: 'Explore the AI Social Media Planner',
    category: 'marketing',
  },
  {
    title: 'AI Cold Email Engine',
    description:
      'Launch multi-step cold email campaigns with mailbox warm-up, AI copy, and CRM-ready analytics without Instantly or agency retainers.',
    to: '/modules/cold-email',
    cta: 'Explore the AI Cold Email Engine',
    category: 'sales',
  },
  {
    title: 'AI Affiliate Marketing Engine',
    description:
      'Recruit partners, automate tiered payouts, and track attribution across your go-to-market stack without juggling Instantly campaigns or agencies.',
    to: '/modules/affiliate-marketing',
    cta: 'Explore the AI Affiliate Marketing Engine',
    category: 'marketing',
  },
  {
    title: 'AI Link Intelligence Suite',
    description:
      'Generate branded QR codes, shorten URLs, and stream analytics to CRM without paying Bitly seat pricing or agency change fees.',
    to: '/modules/link-intelligence',
    cta: 'Explore the AI Link Intelligence Suite',
    category: 'tools',
  },
  {
    title: 'AI Finance Command Center',
    description:
      'Forecast cash, model break-even points, and automate reporting without juggling fragile spreadsheets or consultant retainers.',
    to: '/modules/financial-planning',
    cta: 'Explore the AI Finance Command Center',
    category: 'finance',
  },
  {
    title: 'AI Pricing Intelligence Suite',
    description:
      'Model prices, track margins, and generate strategy playbooks without spreadsheet errors or agency retainers.',
    to: '/modules/pricing-intelligence',
    cta: 'Explore the AI Pricing Intelligence Suite',
    category: 'finance',
  },
  {
    title: 'AI Revenue Operations Hub',
    description:
      'Track sales, monitor spend, and surface runway alerts without stitching spreadsheets or waiting on finance consultants.',
    to: '/modules/revenue-operations',
    cta: 'Explore the AI Revenue Operations Hub',
    category: 'finance',
  },
  {
    title: 'AI Supplier Operations Hub',
    description:
      'Source trade suppliers, manage CRM pipelines, and automate compliance without spreadsheet trackers or procurement retainers.',
    to: '/modules/supplier-operations',
    cta: 'Explore the AI Supplier Operations Hub',
    category: 'operations',
  },
  {
    title: 'AI Risk Management Command Center',
    description:
      'Score risks, assign mitigation, and publish executive dashboards without spreadsheet chaos or enterprise GRC retainers.',
    to: '/modules/risk-management',
    cta: 'Explore the AI Risk Management Command Center',
    category: 'operations',
  },
  {
    title: 'AI Investor CRM & Fundraising Hub',
    description:
      'Manage investor outreach, track pipeline stages, and automate follow-ups without spreadsheet chaos or investor finder retainers.',
    to: '/modules/investor-crm',
    cta: 'Explore the AI Investor CRM & Fundraising Hub',
    category: 'finance',
  },
  {
    title: 'AI Legal & HR Document Hub',
    description:
      'Generate compliant policies, contracts, and playbooks without copying templates or hiring legal and HR advisors.',
    to: '/modules/legal-hr',
    cta: 'Explore the AI Legal & HR Document Hub',
    category: 'hr',
  },
  {
    title: 'AI Project Management Hub',
    description:
      'Manage projects with AI task generation, Kanban boards, Gantt charts, and team workload tracking—without Monday.com or Asana per-seat pricing.',
    to: '/modules/project-management',
    cta: 'Explore the AI Project Management Hub',
    category: 'operations',
  },
  {
    title: 'AI Staff Management & HR Hub',
    description:
      'Manage staff records, time tracking, performance reviews, leave accruals, org charts, and onboarding—without BambooHR or Gusto per-employee pricing.',
    to: '/modules/staff-management',
    cta: 'Explore the AI Staff Management & HR Hub',
    category: 'hr',
  },
  {
    title: 'AI Recruitment & ATS Platform',
    description:
      'White-label careers portal, AI resume screening, pipeline automation, and interview scheduling—without Greenhouse or Lever per-seat fees.',
    to: '/modules/recruitment',
    cta: 'Explore the AI Recruitment & ATS Platform',
    category: 'hr',
  },
  {
    title: 'SEO Content Hub',
    description:
      'Complete SEO workflow: Generate topical authority maps, create optimized blog posts with keyword control, and manage your content library all in one place.',
    to: '/modules/seo-content-hub',
    cta: 'Explore the SEO Content Hub',
    category: 'marketing',
  },
  {
    title: 'SEO Tools Hub',
    description:
      'Analyze your real Google & Bing data for maximum SEO accuracy. Free technical audits, Search Console insights, and keyword research using official data sources. Advanced features like daily ranking tracking, backlink monitoring, and competitor analysis coming soon.',
    to: '/modules/seo-tools-hub',
    cta: 'Explore the SEO Tools Hub',
    category: 'marketing',
  },
  {
    title: 'Ecommerce Hub',
    description:
      'AI-powered product content generation: Create compelling product descriptions, titles, and SEO-optimized copy that converts visitors into customers.',
    to: '/modules/ecommerce-hub',
    cta: 'Explore the Ecommerce Hub',
    category: 'tools',
  },
];

// Pricing information
export const pricingInfo = {
  free: {
    name: 'Free Trial',
    price: '£0',
    duration: '7 days',
    features: [
      'Access to all 23 modules',
      'Limited usage per module',
      'No credit card required',
    ],
  },
  single: {
    name: 'Single User',
    price: '£19',
    duration: 'per user per month',
    features: [
      'Access to all 23 modules',
      'Perfect for solopreneurs',
      'Priority support',
      'Advanced analytics',
    ],
  },
  team: {
    name: 'Team (3 Users)',
    price: '£49',
    duration: 'per month',
    features: [
      '3 users included',
      'Access to all 23 modules',
      '£15 per additional user/month',
      'Priority support',
      'Advanced analytics',
      'API access',
      'White-label options',
    ],
  },
};

// Common questions and answers for chatbot
export const faqKnowledge = {
  'What is Explanatum?': 
    'Explanatum is a complete AI business platform with 23 integrated modules covering strategy, marketing, sales, finance, operations, and HR. It replaces 10+ separate SaaS subscriptions with one unified platform.',
  
  'How much does it cost?':
    'Pricing: £19/user/month for single users, or £49/month for teams (3 users included, £15 per additional user). Access to all 23 modules. 7-day free trial available with no credit card required.',
  
  'What modules are included?':
    'We have 23 AI-powered modules including Business Planning, CRM, Cold Email, Social Media Planner, Financial Planning, HR Management, Project Management, SEO Tools, and more. Each module is fully integrated with the others.',
  
  'How is this different from other tools?':
    'Unlike competitors, Explanatum provides an all-in-one platform where every tool integrates seamlessly. You avoid paying per-seat fees, juggling multiple logins, and dealing with integration headaches.',
  
  'Do you offer a free trial?':
    'Yes! We offer a 7-day free trial with access to all modules. No credit card required.',
  
  'Can I integrate with existing tools?':
    'Yes, we integrate with popular platforms including Google, LinkedIn, WhatsApp, and more. Our API also allows custom integrations.',
};

// Generate system prompt with all product knowledge
export const generateProductKnowledgePrompt = (): string => {
  const modulesList = modules.map(m => `- **${m.title}**: ${m.description}`).join('\n');
  
  return `You are Explanatum's product assistant. Answer questions concisely and professionally.

## Platform Overview
Explanatum is a complete AI business platform with 23 integrated modules. Replace 10+ SaaS subscriptions with one unified platform.

## Pricing
- **Free Trial**: 7 days, all modules, no credit card required
- **Single User**: £19/user/month - Access to all 23 modules, perfect for solopreneurs
- **Team Plan**: £49/month (3 users included) - Each additional user £15/month, access to all 23 modules

## All 23 Modules

${modulesList}

## Key Benefits
- All-in-one platform (no juggling multiple tools)
- One fixed price (no per-seat fees)
- Seamless integration between modules
- AI-powered automation throughout
- Replace tools like Monday.com, HubSpot, BambooHR, Asana, and more

## Integration
We integrate with Google, LinkedIn, WhatsApp, and more. API available for custom integrations.

Guidelines:
- Be helpful and concise
- Guide users to sign up for free trial when appropriate
- Explain how modules work together
- Emphasize cost savings vs buying separate tools`;
};
